import { useEffect, useRef, useState } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Flex,
  Input,
  PasswordInput
} from "@theorgchartdev/storybook";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import { TestSharepointConnection } from "@/Utils/Promises";
import { appstr } from "@coreapp/utils/appstr";

const mapTestErrorToString = code => {
  if (!code) return "";
  if (code === "auth_failed") return appstr("sharepoint_test_auth_failed");
  if (code === "unreachable") return appstr("sharepoint_test_unreachable");
  if (code.startsWith("folder_check_failed")) return appstr("sharepoint_test_folder_failed");
  return appstr("sharepoint_test_unknown");
};

export const SharePointConfig = ({ show, onClose, updateCreds, existingCredsObj = null }) => {
  const [editingCreds, setEditingCreds] = useState({
    domain: "",
    site: "",
    tenantID: "",
    clientID: "",
    clientSecret: ""
  });
  const [testStatus, setTestStatus] = useState({ phase: "idle", error: "" });
  const testInvocationRef = useRef(0);

  useEffect(() => {
    if (existingCredsObj) setEditingCreds(existingCredsObj);
  }, []);

  const HandleInput = (key, value) => {
    setEditingCreds(prevState => ({ ...prevState, [key]: value }));
    testInvocationRef.current += 1;
    if (testStatus.phase !== "idle") setTestStatus({ phase: "idle", error: "" });
  };

  const canTest =
    !!editingCreds.domain &&
    !!editingCreds.tenantID &&
    !!editingCreds.clientID &&
    !!editingCreds.clientSecret &&
    testStatus.phase !== "testing";

  const runTest = async () => {
    testInvocationRef.current += 1;
    const myInvocation = testInvocationRef.current;
    setTestStatus({ phase: "testing", error: "" });
    const { success, error } = await TestSharepointConnection(editingCreds);
    if (testInvocationRef.current !== myInvocation) return;
    setTestStatus({ phase: success ? "success" : "error", error: error || "" });
  };

  const renderTestStatus = () => {
    if (testStatus.phase === "testing") {
      return <span>{appstr("sharepoint_test_in_progress")}</span>;
    }
    if (testStatus.phase === "success") {
      return (
        <span style={{ color: "var(--text-success, #1d7e3e)" }}>
          {"✓ " + appstr("sharepoint_test_ok")}
        </span>
      );
    }
    if (testStatus.phase === "error") {
      return (
        <span style={{ color: "var(--text-danger, #b91c1c)" }}>
          {"✗ " + mapTestErrorToString(testStatus.error)}
        </span>
      );
    }
    return null;
  };

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        show={show}
        centerOnScreen={true}
        fullScreen={false}
        customStyle={{ padding: 0, width: "640px" }}
      >
        <DialogTitle
          divider={true}
          onClose={() => onClose()}
          data-testid="account-settings-authorization-sharepoint-close-btn"
        >
          {appstr("Sharepoint Configuration")}
        </DialogTitle>

        <DialogContent>
          <Flex direction={"column"} gap={"8px"} padding={"8px 0px 0px"}>
            <Input
              key={"auth-shrpt-0"}
              dataTestId="account-settings-authorization-sharepoint-domain-input"
              label={appstr("Domain")}
              value={editingCreds.domain}
              size={"medium"}
              onChange={e => HandleInput("domain", e.target.value)}
            />
            <Input
              key={"auth-shrpt-1"}
              dataTestId="account-settings-authorization-sharepoint-site-input"
              label={appstr("Site")}
              value={editingCreds.site}
              size={"medium"}
              onChange={e => HandleInput("site", e.target.value)}
            />
            <Input
              key={"auth-shrpt-2"}
              dataTestId="account-settings-authorization-sharepoint-tenant-input"
              label={appstr("Tenant ID")}
              value={editingCreds.tenantID}
              size={"medium"}
              onChange={e => HandleInput("tenantID", e.target.value)}
            />
            <Input
              key={"auth-shrpt-3"}
              dataTestId="account-settings-authorization-sharepoint-client-id-input"
              label={appstr("Client ID")}
              value={editingCreds.clientID}
              size={"medium"}
              onChange={e => HandleInput("clientID", e.target.value)}
            />
            <PasswordInput
              key={"auth-shrpt-4"}
              dataTestId="account-settings-authorization-sharepoint-client-secret-input"
              label={appstr("Client secret")}
              value={editingCreds.clientSecret}
              size={"medium"}
              onChange={e => HandleInput("clientSecret", e.target.value)}
            />
            <Flex direction={"row"} gap={"12px"} padding={"8px 0px 0px"}>
              <Button
                modifier={"secondary"}
                disabled={!canTest}
                onClick={runTest}
                dataTestId="account-settings-authorization-sharepoint-test-btn"
              >
                {appstr("Test Connection")}
              </Button>
              <Flex direction={"column"} justify={"center"}>
                {renderTestStatus()}
              </Flex>
            </Flex>
          </Flex>
        </DialogContent>
        <DialogActions divider={true}>
          <DialogActions.Tertiary
            onClick={() => onClose()}
            dataTestId="account-settings-authorization-sharepoint-cancel-btn"
          >
            {appstr("Cancel")}
          </DialogActions.Tertiary>
          <DialogActions.Primary
            disabled={editingCreds.domain === ""}
            onClick={() => updateCreds(editingCreds)}
            dataTestId="account-settings-authorization-sharepoint-save-btn"
          >
            {appstr("Save")}
          </DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};
